import getPercentageOfDocumentForElement from '../getPercentageOfDocumentForElement';
import getPixelsForPercentageOfDocument from '../getPixelsForPercentageOfDocument';

/**
 * @method getLandmarkForPercentageOfDocument
 * @param {Element} frame - iframe element
 * @param {BookDocument} document - a BookDocument that contains instances of the specified landmark type
 * @param {Number} percentageOfDocument - the percentage between 0 and 1 of the given document
 * @param {String} landmarkName - type of landmark: pages or markers
 */
const getLandmarkForPercentageOfDocument = (
  frame,
  document,
  percentageOfDocument,
  landmarkName = 'pages',
) => {
  if (!frame || !document) {
    return;
  }
  if (isNaN(parseFloat(percentageOfDocument))) {
    throw new Error('percentageOfDocument is required to find landmarks');
  }

  // If there are instances of this type of landmark in this document,
  // try to compute the correct landmark using the percentageOfDocument.
  if (document[landmarkName].length) {
    const frameDocument = frame.contentDocument;

    let closestLandmark;

    for (let landmark of document[landmarkName]) {
      if (!landmark) {
        console.warn(
          `The document has a missing ${
            landmarkName === 'pages' ? 'page' : 'chapter'
          } object.`,
          document[landmarkName],
        );
        continue;
      }

      if (landmark.element === '') {
        /**
         * If a landmark with an empty string for an element name exists in the document, assume it points to the top of the document.
         *
         * This way, unless the relevant percentageOfDocument points to somewhere in the document after the position of another marker
         * (with a defined element name) it will default to pointing to the marker with the empty element string.
         *
         * If there are multiple landmarks with an empty string for an element name, it will use the one with the higher index.
         * However, this shouldn't happen. Leaving the element name as an empty string to point to the beginning of the document
         * is reasonable enough. A document with multiple landmarks that have empty element strings would be asking for trouble.
         */
        closestLandmark = landmark;
        continue;
      }

      const landmarkElement = frameDocument.getElementById(landmark.element);
      if (!landmarkElement) {
        // A landmark element is supposed to exist, but either it is missing from the document or the document itself is broken.
        console.warn(
          `A ${
            landmarkName === 'pages' ? 'page' : 'chapter'
          } with element id "${
            landmark.element
          }" is supposed to exist in document with index ${
            document.index
          }, but no such element was found.`,
        );
        // Best we can do here is pretend it doesn't exist and continue on.
        continue;
      }

      // Get the percentage for the landmark
      const landmarkPercentage = getPercentageOfDocumentForElement(
        frame,
        landmarkElement,
      );

      // Convert to pixels
      const pixelOffsetForLandmark = getPixelsForPercentageOfDocument(
        frame,
        landmarkPercentage,
      );

      // Is the landmark's position before, or equal to, the target position?
      // If so, it will be the closest landmark unless we go on to find a later one that is equally close or even closer to the target.
      const pixelOffsetForPercentageOfDocument =
        getPixelsForPercentageOfDocument(frame, percentageOfDocument);

      if (pixelOffsetForLandmark <= pixelOffsetForPercentageOfDocument) {
        closestLandmark = landmark;
        continue;
      }

      break;
    }

    if (closestLandmark) {
      return closestLandmark;
    }
  }

  // If there are no instances of the specified landmark type on the current document that start
  // before the given percentage, iterate backwards to find the landmark in a previous document.
  let previousDocument = document.previous;

  while (previousDocument) {
    if (previousDocument[landmarkName].length) {
      return previousDocument[landmarkName][
        previousDocument[landmarkName].length - 1
      ];
    }

    previousDocument = previousDocument.previous;
  }
};

export default getLandmarkForPercentageOfDocument;
